import React, { useState, useEffect, useCallback } from 'react';
import { getPrayerComments, addPrayerComment, PrayerComment } from '../services/sharePointService';
import {
  Textarea,
  Button,
  makeStyles,
  tokens,
  Spinner,
  Card,
  CardHeader,
  Avatar,
  Caption1,
  Body1,
  Subtitle2,
} from '@fluentui/react-components';
import { SendRegular, PersonRegular } from '@fluentui/react-icons';

// Using PrayerComment type from SharePoint service

interface PrayerCommentBoxProps {
  prayerId: number;
  onCommentAdded?: () => void; // Callback when a comment is successfully added
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '600px',
  },
  commentCard: {
    marginBottom: '12px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '8px',
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  commentAuthor: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  commentDate: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  commentText: {
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '8px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  textarea: {
    minHeight: '80px',
    resize: 'vertical',
  },
  submitButton: {
    alignSelf: 'flex-end',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: tokens.colorNeutralForeground3,
  },
  errorMessage: {
    color: tokens.colorStatusDangerForeground1,
    padding: '12px',
    backgroundColor: tokens.colorStatusDangerBackground1,
    borderRadius: '4px',
    marginBottom: '12px',
  },
});

const PrayerCommentBox: React.FC<PrayerCommentBoxProps> = ({ prayerId, onCommentAdded }) => {
  const styles = useStyles();
  const [comments, setComments] = useState<PrayerComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch comments for the specific prayer
  const fetchComments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const commentsData = await getPrayerComments(prayerId);
      setComments(commentsData);
    } catch (err) {
      console.error('Error fetching comments:', err);
      
      // Provide more specific error messages based on the error type
      let errorMessage = 'Failed to load comments. Please try again.';
      
      if (err instanceof Error) {
        if (err.message.includes('403') || err.message.includes('Forbidden')) {
          errorMessage = 'Access denied. You may need additional permissions to view comments.';
        } else if (err.message.includes('404') || err.message.includes('Not Found')) {
          errorMessage = 'Comments list not found. The list may need to be created by an administrator.';
        } else if (err.message.includes('Network') || err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [prayerId]);

  // Submit a new comment
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Submit to SharePoint
      await addPrayerComment(prayerId, newComment.trim());
      
      // Refresh comments to show the new one
      await fetchComments();
      
      // Clear the input
      setNewComment('');
      
      // Notify parent component to refresh prayer list
      onCommentAdded?.();
    } catch (err) {
      console.error('Error submitting comment:', err);
      
      // Provide more specific error messages for comment submission
      let errorMessage = 'Failed to submit comment. Please try again.';
      
      if (err instanceof Error) {
        if (err.message.includes('403') || err.message.includes('Forbidden')) {
          errorMessage = 'Access denied. You may need additional permissions to add comments.';
        } else if (err.message.includes('404') || err.message.includes('Not Found')) {
          errorMessage = 'Comments list not found. The list may need to be created by an administrator.';
        } else if (err.message.includes('Network') || err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('validation') || err.message.includes('required')) {
          errorMessage = 'Please ensure your comment meets the requirements.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Load comments on component mount and when prayerId changes
  useEffect(() => {
    if (prayerId) {
      fetchComments();
    }
  }, [prayerId, fetchComments]);

  // Handle Enter key to submit (Shift+Enter for new line)
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmitComment();
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="medium" label="Loading comments..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error && (
        <div className={styles.errorMessage}>
          {error}
          {error.includes('list not found') && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              💡 Tip: An administrator can create the required lists in SharePoint.
            </div>
          )}
        </div>
      )}

      {/* Comments Section */}
      <div>
        {comments.length === 0 ? (
          <div className={styles.emptyState}>
            <Body1>No comments yet. Be the first to share your thoughts!</Body1>
          </div>
        ) : (
          comments.map((comment) => (
            <Card key={comment.Id} className={styles.commentCard}>
              <CardHeader>
                <div className={styles.commentHeader}>
                  <Avatar
                    name={comment.CreatedBy.Title}
                    size={24}
                    icon={<PersonRegular />}
                  />
                  <div>
                    <Subtitle2 className={styles.commentAuthor}>
                      {comment.CreatedBy.Title}
                    </Subtitle2>
                    <Caption1 className={styles.commentDate}>
                      {formatDate(comment.Created)}
                    </Caption1>
                  </div>
                </div>
              </CardHeader>
                             <div style={{ padding: '12px 16px' }}>
                 <Body1 className={styles.commentText}>
                   {comment.CommentText}
                 </Body1>
               </div>
            </Card>
          ))
        )}
      </div>

      {/* Add Comment Section */}
      <div className={styles.inputSection}>
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Share your thoughts on this prayer..."
          className={styles.textarea}
          disabled={isSubmitting}
        />
        <Button
          appearance="primary"
          icon={<SendRegular />}
          onClick={handleSubmitComment}
          disabled={!newComment.trim() || isSubmitting}
          className={styles.submitButton}
        >
          {isSubmitting ? 'Posting...' : 'Post Comment'}
        </Button>
      </div>
    </div>
  );
};

export default PrayerCommentBox; 